import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { http, errText } from "@/lib/api";
import { StatCards, VehicleBars } from "./AdminStats";
import { SignupTable } from "./SignupTable";
import { TopReferrers } from "./TopReferrers";

export const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([http.get("/admin/stats"), http.get("/admin/signups")]);
      setStats(s.data ?? null);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error(errText(e));
    }
  }, [onLogout]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => rows.filter((r) =>
    (role === "all" || r.role === role) &&
    [r.name, r.email, r.zip, r.vehicle_type].join(" ").toLowerCase().includes(q.toLowerCase())), [rows, q, role]);

  const exportCsv = async () => {
    try {
      const res = await http.get("/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marketgetr-signups-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sign-up?")) return;
    try { await http.delete(`/admin/signups/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="admin-dashboard">
      <header className="bg-[#13296B] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-display text-2xl font-extrabold">Sign-up monitor</p>
            <p className="text-sm text-slate-300">{user.email} · auto-refreshes every 30s</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="rounded-full border border-white/25 px-4 py-2 text-sm font-bold hover:bg-white/10 inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="rounded-full bg-[#2F7FF0] px-4 py-2 text-sm font-bold hover:bg-white hover:text-[#13296B] inline-flex items-center gap-2 transition-colors"><Download className="h-4 w-4" /> Export CSV</button>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="rounded-full border border-white/25 px-4 py-2 text-sm font-bold hover:bg-white/10 inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-8 space-y-6">
        <StatCards stats={stats} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopReferrers list={Array.isArray(stats?.top_referrers) ? stats.top_referrers : []} />
          <VehicleBars vehicles={stats?.vehicles ?? {}} />
        </div>
        <SignupTable rows={filtered} total={rows.length} q={q} setQ={setQ} role={role} setRole={setRole} onDelete={remove} />
      </main>
    </div>
  );
};
